import moment from 'moment';
import { t } from 'ttag';
import { STUDY_TYPES, FOLLOW_ON_STUDY_TYPE } from './studyConstants';

/**
 * Validates a study duration entered in minutes against the limits of its study type and device.
 * Holter and follow-on studies in hours must span 1 to 72 hours, other studies 1 to 3, 7 or 30
 * days in whole days, and an editable running study must outlast its elapsed time.
 *
 * @param studyDuration - Requested duration in minutes.
 * @param studyType - Study type that sets the maximum duration.
 * @param unitTime - Unit the duration was entered in, hour or day.
 * @param isHighSamplingRate - Whether the device records at high sampling rate, capping it at 7 days.
 * @param startTime - Start of the study, used to compare against elapsed time.
 * @param isDisabledEdit - Whether editing is disabled, skipping the elapsed-time check.
 * @returns The translated validation message, or undefined when the duration is valid.
 */
const checkStudyDuration = ({
  studyDuration,
  studyType = null,
  unitTime,
  isHighSamplingRate = false,
  startTime = null,
  isDisabledEdit = false,
}: any) => {
  const maxDuration =
    studyType && (studyType === STUDY_TYPES.HOLTER || studyType === FOLLOW_ON_STUDY_TYPE)
      ? 3
      : isHighSamplingRate
        ? 7
        : 30;

  // For customer environment
  const isHolterStudy = [STUDY_TYPES.HOLTER, FOLLOW_ON_STUDY_TYPE].includes(studyType);
  // *: For testing (CP-2.24.1)
  // const isHolterStudy = true;

  const isUnitHours = unitTime === 'hour';
  const nowFromStart = moment().diff(moment(startTime), 'minutes');
  if (isHolterStudy && isUnitHours) {
    if (!studyDuration || studyDuration > 72 * 60 || studyDuration < 1 * 60) {
      // *: The duration must be between 1 and 72 hours for HOLTER study.
      return `${t`The duration must be between 1 and 72 hours for HOLTER study.`}`;
    }
    if (
      startTime &&
      studyDuration &&
      nowFromStart &&
      studyDuration <= nowFromStart &&
      !isDisabledEdit
    ) {
      return `${t`Please enter a duration greater than the time the study has been running.`}`;
    }

    return undefined;
  }

  if (!studyDuration || studyDuration > maxDuration * 1440 || studyDuration < 1 * 1440) {
    const isFollowOnStudy = studyType === FOLLOW_ON_STUDY_TYPE;
    // *: The duration must be between 1 and 3 days for HOLTER study.
    return isHighSamplingRate && !isHolterStudy
      ? t`Study duration limited to 7 days.`
      : `${t`The duration must be between 1 and`} ${maxDuration} ${t`days for`} ${isFollowOnStudy ? STUDY_TYPES.HOLTER : studyType} ${t`study.`}`;
  }

  if (
    startTime &&
    studyDuration &&
    nowFromStart &&
    studyDuration <= nowFromStart &&
    !isDisabledEdit
  ) {
    return `${t`Please enter a duration greater than the time the study has been running.`}`;
  }

  if (studyDuration && studyDuration % 1440 !== 0) {
    return `${t`Please enter an integer value`}`;
  }

  return undefined;
};

export { checkStudyDuration };
